import { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import {
  ShieldCheckmark,
  TrendingUp,
  Wallet,
  AlertCircle,
  CheckmarkCircle,
  CloseCircle,
  TimeOutline,
  DocumentText,
  RefreshCircle,
  WarningOutline,
} from "@raresail/react-ionicons";
import { Divider, Heading } from "../../elements/Elements";
import useGet from "../../../hooks/common/useGet";
import { IsBorrower } from "../../../utils/permissionUtils";
import { currencyFormat, numberFormat } from "../../../utils/helpers";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CreditProfile {
  id: string;
  user: string;
  email: string;
  name: string;
  total_loans_taken: number;
  total_loans_completed_on_time: number;
  total_loans_completed_late: number;
  total_loans_defaulted: number;
  total_loans_written_off: number;
  total_loans_restructured: number;
  total_amount_borrowed: string;
  total_amount_repaid: string;
  total_penalties_incurred: string;
  average_days_overdue: string;
  internal_credit_score: number;
  everpesa_score: number;
  risk_grade: string;
  assigned_interest_rate: string;
  rate_by_duration: Record<string, string>;
  last_updated: string;
}

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulseRing = keyframes`
  0%   { box-shadow: 0 0 0 0px rgba(99,102,241,0.35); }
  70%  { box-shadow: 0 0 0 14px rgba(99,102,241,0); }
  100% { box-shadow: 0 0 0 0px rgba(99,102,241,0); }
`;

const fillBar = keyframes`
  from { width: 0%; }
  to   { width: var(--fill); }
`;

const countUp = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to   { opacity: 1; transform: scale(1); }
`;

// ─── Grade helpers ─────────────────────────────────────────────────────────────

const GRADE_META: Record<
  string,
  { color: string; bg: string; label: string; tier: number }
> = {
  A1: { color: "#10b981", bg: "#d1fae5", label: "Excellent", tier: 1 },
  A2: { color: "#10b981", bg: "#d1fae5", label: "Excellent", tier: 1 },
  A3: { color: "#10b981", bg: "#d1fae5", label: "Excellent", tier: 1 },
  A4: { color: "#10b981", bg: "#d1fae5", label: "Excellent", tier: 1 },
  A5: { color: "#34d399", bg: "#d1fae5", label: "Very Good", tier: 2 },
  B1: { color: "#34d399", bg: "#d1fae5", label: "Very Good", tier: 2 },
  B2: { color: "#6ee7b7", bg: "#ecfdf5", label: "Good", tier: 2 },
  B3: { color: "#6ee7b7", bg: "#ecfdf5", label: "Good", tier: 2 },
  B4: { color: "#6ee7b7", bg: "#ecfdf5", label: "Good", tier: 2 },
  B5: { color: "#facc15", bg: "#fef9c3", label: "Fair", tier: 3 },
  C1: { color: "#facc15", bg: "#fef9c3", label: "Fair", tier: 3 },
  C2: { color: "#facc15", bg: "#fef9c3", label: "Fair", tier: 3 },
  C3: { color: "#fb923c", bg: "#ffedd5", label: "Below Average", tier: 3 },
  C4: { color: "#fb923c", bg: "#ffedd5", label: "Below Average", tier: 3 },
  C5: { color: "#fb923c", bg: "#ffedd5", label: "Below Average", tier: 3 },
  D1: { color: "#f87171", bg: "#fee2e2", label: "Poor", tier: 4 },
  D2: { color: "#f87171", bg: "#fee2e2", label: "Poor", tier: 4 },
  D3: { color: "#f87171", bg: "#fee2e2", label: "Poor", tier: 4 },
  D4: { color: "#ef4444", bg: "#fee2e2", label: "Very Poor", tier: 4 },
  D5: { color: "#ef4444", bg: "#fee2e2", label: "Very Poor", tier: 4 },
  E1: { color: "#dc2626", bg: "#fecaca", label: "High Risk", tier: 5 },
  E2: { color: "#dc2626", bg: "#fecaca", label: "High Risk", tier: 5 },
  E3: { color: "#b91c1c", bg: "#fecaca", label: "High Risk", tier: 5 },
  E4: { color: "#b91c1c", bg: "#fecaca", label: "High Risk", tier: 5 },
  E5: { color: "#991b1b", bg: "#fecaca", label: "Critical Risk", tier: 5 },
};

const getGradeMeta = (grade: string) =>
  GRADE_META[grade] || { color: "#6366f1", bg: "#ede9fe", label: "Unrated", tier: 3 };

// Everpesa score 1 (best) → 25 (worst). Convert to 0–100 "good" percentage.
const scoreToPercent = (score: number) =>
  Math.round(((25 - score) / 24) * 100);

// ─── Sub-components ───────────────────────────────────────────────────────────

const ScoreMeter = ({ score, grade }: { score: number; grade: string }) => {
  const pct = scoreToPercent(score);
  const meta = getGradeMeta(grade);

  return (
    <MeterWrapper>
      {/* Arc gauge */}
      <GaugeOuter style={{ borderColor: meta.bg }}>
        <GaugeInner
          style={{
            background: `conic-gradient(${meta.color} ${pct * 1.8}deg, #f1f5f9 0deg)`,
          }}
        >
          <GaugeCenter>
            <ScoreNum style={{ color: meta.color }}>{score}</ScoreNum>
            <ScoreOf>/ 25</ScoreOf>
          </GaugeCenter>
        </GaugeInner>
      </GaugeOuter>

      <GradeChip style={{ background: meta.bg, color: meta.color }}>
        <ShieldCheckmark fontSize={13} />
        <span style={{ fontWeight: 700, letterSpacing: "0.03em" }}>{grade}</span>
        <span style={{ opacity: 0.85 }}>· {meta.label}</span>
      </GradeChip>
    </MeterWrapper>
  );
};

const StatCard = ({
  icon: Icon,
  label,
  value,
  color,
  delay = 0,
}: {
  icon: any;
  label: string;
  value: string | number;
  color: string;
  delay?: number;
}) => (
  <StatCardWrap style={{ animationDelay: `${delay}ms` }}>
    <StatIcon style={{ background: `${color}18`, color }}>
      <Icon fontSize={16} />
    </StatIcon>
    <StatValue>{value}</StatValue>
    <StatLabel>{label}</StatLabel>
  </StatCardWrap>
);

const LoanBar = ({
  label,
  value,
  total,
  color,
  icon: Icon,
  delay = 0,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
  icon: any;
  delay?: number;
}) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <LoanBarWrap style={{ animationDelay: `${delay}ms` }}>
      <LoanBarHeader>
        <LoanBarLeft>
          <LoanBarIcon style={{ color, background: `${color}15` }}>
            <Icon fontSize={13} />
          </LoanBarIcon>
          <span className="font12">{label}</span>
        </LoanBarLeft>
        <LoanBarRight style={{ color }}>
          {value} <span style={{ color: "#94a3b8", fontWeight: 400 }}>({pct}%)</span>
        </LoanBarRight>
      </LoanBarHeader>
      <BarTrack>
        <BarFill
          style={
            {
              "--fill": `${pct}%`,
              background: color,
            } as any
          }
        />
      </BarTrack>
    </LoanBarWrap>
  );
};

const RateDurationTable = ({
  rates,
}: {
  rates: Record<string, string>;
}) => {
  const entries = Object.entries(rates);
  if (!entries.length) return null;

  return (
    <RateTable>
      <RateTableHead>
        <tr>
          <th>Duration</th>
          <th>Monthly Rate</th>
        </tr>
      </RateTableHead>
      <tbody>
        {entries.map(([duration, rate]) => (
          <RateTableRow key={duration}>
            <td>{duration} days</td>
            <td>
              <RateChip>{parseFloat(rate).toFixed(2)}%</RateChip>
            </td>
          </RateTableRow>
        ))}
      </tbody>
    </RateTable>
  );
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const Skeleton = styled.div<{ w?: string; h?: string }>`
  width: ${({ w }) => w || "100%"};
  height: ${({ h }) => h || "14px"};
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s infinite;
`;

const SkeletonBlock = () => (
  <div className="flexColumn gap10" style={{ padding: "4px 0" }}>
    <Skeleton h="120px" />
    <div className="flex gap10">
      <Skeleton h="70px" />
      <Skeleton h="70px" />
      <Skeleton h="70px" />
    </div>
    <Skeleton h="90px" />
    <Skeleton h="160px" />
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export const CreditProfileCard = () => {
  const { loadData, data, loading } = useGet();
  const isBorrower = IsBorrower();

  useEffect(() => {
    if (isBorrower) {
      loadData({ api: "me/credit-profile/" });
    }
  }, []);

  if (!isBorrower) return null;

  const profile = data as CreditProfile | null;

  return (
    <Section>
      <SectionHeader>
        <HeaderLeft>
          <HeaderIcon>
            <TrendingUp fontSize={18} />
          </HeaderIcon>
          <Heading>Credit Profile</Heading>
        </HeaderLeft>
        {profile && (
          <LastUpdated>
            Updated{" "}
            {new Date(profile.last_updated).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </LastUpdated>
        )}
      </SectionHeader>

      <Divider />

      {loading && <SkeletonBlock />}

      {!loading && !profile && (
        <EmptyState>
          <AlertCircle fontSize={36} color="#94a3b8" />
          <p className="grey font13">Credit profile unavailable.</p>
        </EmptyState>
      )}

      {!loading && profile && (
        <ProfileBody>
          {/* ── Score + Grade ───────────────────────────── */}
          <ScoreSection>
            <ScoreMeter
              score={profile.everpesa_score}
              grade={profile.risk_grade}
            />
            <ScoreDetails>
              <ScoreDetailItem>
                <ScoreDetailLabel>Base Interest Rate</ScoreDetailLabel>
                <ScoreDetailValue accent>
                  {parseFloat(profile.assigned_interest_rate).toFixed(2)}%
                  <ScoreDetailSub>/month</ScoreDetailSub>
                </ScoreDetailValue>
              </ScoreDetailItem>
              <SDivider />
              <ScoreDetailItem>
                <ScoreDetailLabel>Internal Score</ScoreDetailLabel>
                <ScoreDetailValue>
                  {numberFormat(profile.internal_credit_score)}
                  <ScoreDetailSub>/ 1000</ScoreDetailSub>
                </ScoreDetailValue>
              </ScoreDetailItem>
              <SDivider />
              <ScoreDetailItem>
                <ScoreDetailLabel>Avg. Days Overdue</ScoreDetailLabel>
                <ScoreDetailValue
                  warn={parseFloat(profile.average_days_overdue) > 0}
                >
                  {parseFloat(profile.average_days_overdue).toFixed(1)}
                  <ScoreDetailSub>days</ScoreDetailSub>
                </ScoreDetailValue>
              </ScoreDetailItem>
            </ScoreDetails>
          </ScoreSection>

          {/* ── Financial Stats ─────────────────────────── */}
          <StatsGrid>
            <StatCard
              icon={Wallet}
              label="Total Borrowed"
              value={currencyFormat(parseFloat(profile.total_amount_borrowed))}
              color="#6366f1"
              delay={0}
            />
            <StatCard
              icon={CheckmarkCircle}
              label="Total Repaid"
              value={currencyFormat(parseFloat(profile.total_amount_repaid))}
              color="#10b981"
              delay={60}
            />
            <StatCard
              icon={WarningOutline}
              label="Penalties"
              value={currencyFormat(
                parseFloat(profile.total_penalties_incurred)
              )}
              color="#f59e0b"
              delay={120}
            />
          </StatsGrid>

          {/* ── Loan Breakdown ──────────────────────────── */}
          <LoanBreakdownCard>
            <LBHeader>
              <DocumentText fontSize={15} color="#6366f1" />
              <span className="font13 semiBold">
                Loan History &nbsp;
                <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                  ({profile.total_loans_taken} total)
                </span>
              </span>
            </LBHeader>

            <LoanBarsWrap>
              <LoanBar
                label="Completed on time"
                value={profile.total_loans_completed_on_time}
                total={profile.total_loans_taken}
                color="#10b981"
                icon={CheckmarkCircle}
                delay={0}
              />
              <LoanBar
                label="Completed late"
                value={profile.total_loans_completed_late}
                total={profile.total_loans_taken}
                color="#f59e0b"
                icon={TimeOutline}
                delay={80}
              />
              <LoanBar
                label="Defaulted"
                value={profile.total_loans_defaulted}
                total={profile.total_loans_taken}
                color="#ef4444"
                icon={CloseCircle}
                delay={160}
              />
              <LoanBar
                label="Restructured"
                value={profile.total_loans_restructured}
                total={profile.total_loans_taken}
                color="#8b5cf6"
                icon={RefreshCircle}
                delay={240}
              />
              <LoanBar
                label="Written off"
                value={profile.total_loans_written_off}
                total={profile.total_loans_taken}
                color="#94a3b8"
                icon={DocumentText}
                delay={320}
              />
            </LoanBarsWrap>
          </LoanBreakdownCard>

          {/* ── Rate by Duration ────────────────────────── */}
          {profile.rate_by_duration &&
            Object.keys(profile.rate_by_duration).length > 0 && (
              <RateDurationCard>
                <LBHeader>
                  <TrendingUp fontSize={15} color="#6366f1" />
                  <span className="font13 semiBold">Rate by Duration</span>
                </LBHeader>
                <RateDurationTable rates={profile.rate_by_duration} />
              </RateDurationCard>
            )}
        </ProfileBody>
      )}
    </Section>
  );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const Section = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #ede9fe;
  color: #6366f1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LastUpdated = styled.div`
  font-size: 11px;
  color: #94a3b8;
  font-style: italic;
`;

const EmptyState = styled.div`
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const ProfileBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: ${fadeUp} 0.4s ease both;
`;

// ── Score ──

const ScoreSection = styled.div`
  display: flex;
  align-items: stretch;
  gap: 14px;
  background: linear-gradient(135deg, #f8faff 0%, #f1f0ff 100%);
  border-radius: 12px;
  padding: 16px;
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const MeterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const GaugeOuter = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 4px solid #ede9fe;
  padding: 5px;
  animation: ${pulseRing} 2.5s ease-out infinite;
`;

const GaugeInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.6s ease;
`;

const GaugeCenter = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ScoreNum = styled.div`
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
  animation: ${countUp} 0.5s ease both;
`;

const ScoreOf = styled.div`
  font-size: 10px;
  color: #94a3b8;
  font-weight: 500;
`;

const GradeChip = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
`;

const ScoreDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0;
`;

const ScoreDetailItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 0;
`;

const ScoreDetailLabel = styled.div`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
`;

const ScoreDetailValue = styled.div<{ accent?: boolean; warn?: boolean }>`
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: baseline;
  gap: 3px;
  color: ${({ accent, warn }) =>
    accent ? "#6366f1" : warn ? "#ef4444" : "#1e293b"};
`;

const ScoreDetailSub = styled.span`
  font-size: 10px;
  color: #94a3b8;
  font-weight: 400;
`;

const SDivider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 2px 0;
`;

// ── Stats grid ──

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const StatCardWrap = styled.div`
  background: #f8fafc;
  border-radius: 10px;
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  animation: ${fadeUp} 0.4s ease both;
`;

const StatIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
`;

const StatValue = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  word-break: break-all;
`;

const StatLabel = styled.div`
  font-size: 10px;
  color: #94a3b8;
  font-weight: 500;
`;

// ── Loan bars ──

const LoanBreakdownCard = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LBHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LoanBarsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LoanBarWrap = styled.div`
  animation: ${fadeUp} 0.4s ease both;
`;

const LoanBarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const LoanBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  color: #475569;
`;

const LoanBarIcon = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoanBarRight = styled.div`
  font-size: 12px;
  font-weight: 700;
`;

const BarTrack = styled.div`
  height: 6px;
  background: #e2e8f0;
  border-radius: 99px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 99px;
  width: 0%;
  animation: ${fillBar} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
`;

// ── Rate table ──

const RateDurationCard = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RateTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`;

const RateTableHead = styled.thead`
  th {
    text-align: left;
    padding: 6px 10px;
    color: #94a3b8;
    font-weight: 600;
    font-size: 11px;
    border-bottom: 1px solid #e2e8f0;
  }
`;

const RateTableRow = styled.tr`
  td {
    padding: 8px 10px;
    color: #475569;
    border-bottom: 1px solid #f1f5f9;
  }
  &:last-child td {
    border-bottom: none;
  }
  &:hover td {
    background: #f1f5f9;
  }
`;

const RateChip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  background: #ede9fe;
  color: #6366f1;
  font-weight: 700;
  font-size: 11px;
`;